import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Save, Sparkles, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { TagInput } from "@/components/TagInput";
import { LanguageMultiSelect, CountryMultiSelect } from "@/components/LocalePickers";

export default function AutofillBrandReview() {
  const { t } = useTranslation(["autofill", "brands", "common", "errors", "competitors"]);
  const location = useLocation();
  const navigate = useNavigate();
  const draftIn = location.state?.draft;
  const sourceUrl = location.state?.source_url;

  const [draft, setDraft] = useState(() => draftIn || null);
  const [includedComps, setIncludedComps] = useState(() =>
    new Set((draftIn?.suggested_competitors || []).map((_, i) => i))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!draftIn) setDraft(null); }, [draftIn]);

  if (!draft) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white px-8 py-16 text-center" data-testid="autofill-review-empty">
        <h2 className="font-display text-xl font-semibold text-slate-900">{t("autofill:review.noDraft.title")}</h2>
        <p className="mt-2 text-sm text-slate-500">{t("autofill:review.noDraft.body")}</p>
        <Button onClick={() => navigate("/brands")} className="mt-6 bg-slate-900 text-white hover:bg-slate-800">{t("autofill:review.noDraft.cta")}</Button>
      </div>
    );
  }

  const toggleComp = (i) => {
    const next = new Set(includedComps);
    if (next.has(i)) next.delete(i); else next.add(i);
    setIncludedComps(next);
  };

  const save = async () => {
    if (!draft.name?.trim()) {
      toast.error(t("errors:validation"));
      return;
    }
    setSaving(true);
    try {
      const filteredDraft = {
        ...draft,
        suggested_competitors: (draft.suggested_competitors || []).filter((_, i) => includedComps.has(i)),
      };
      const { data } = await api.post("/brands/from_autofill", {
        draft: filteredDraft,
        include_competitors: true,
      });
      toast.success(t("autofill:results.brandCreated"));
      navigate(`/brands/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="autofill-brand-review-page">
      <button
        type="button"
        onClick={() => navigate("/brands")}
        data-testid="autofill-review-back"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("common:back")}
      </button>

      <header className="space-y-3">
        <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
          <Sparkles className="mr-1 h-3 w-3" strokeWidth={2} /> {t("autofill:aiBadge")}
        </Badge>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {t("autofill:review.brand.title")}
        </h1>
        <p className="max-w-2xl text-sm text-slate-500">
          {t("autofill:review.brand.subtitle", { url: sourceUrl })}
        </p>
      </header>

      <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.nameLabel")}</Label>
            <Input required value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="autofill-brand-name" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.urlLabel")}</Label>
            <Input type="url" value={draft.url || ""} onChange={(e) => setDraft({ ...draft, url: e.target.value })} data-testid="autofill-brand-url" className="h-11" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.industryLabel")}</Label>
            <Input value={draft.industry || ""} onChange={(e) => setDraft({ ...draft, industry: e.target.value })} data-testid="autofill-brand-industry" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.audienceLabel")}</Label>
            <Input value={draft.audience || ""} onChange={(e) => setDraft({ ...draft, audience: e.target.value })} data-testid="autofill-brand-audience" className="h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.positioningLabel")}</Label>
          <Textarea value={draft.positioning || ""} onChange={(e) => setDraft({ ...draft, positioning: e.target.value })} data-testid="autofill-brand-positioning" rows={3} />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.languagesLabel")}</Label>
            <LanguageMultiSelect value={draft.languages || []} onChange={(v) => setDraft({ ...draft, languages: v })} dataTestid="autofill-brand-languages" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("brands:form.countriesLabel")}</Label>
            <CountryMultiSelect value={draft.countries || []} onChange={(v) => setDraft({ ...draft, countries: v })} dataTestid="autofill-brand-countries" />
          </div>
        </div>
      </div>

      {(draft.suggested_competitors || []).length > 0 && (
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <header className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("autofill:review.competitors.title")}</h2>
            <p className="text-sm text-slate-500">{t("autofill:review.competitors.subtitle")}</p>
          </header>
          <div className="space-y-2">
            {draft.suggested_competitors.map((c, i) => (
              <label key={i} className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${includedComps.has(i) ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white"}`} data-testid={`autofill-brand-comp-${i}`}>
                <Switch checked={includedComps.has(i)} onCheckedChange={() => toggleComp(i)} data-testid={`autofill-brand-comp-toggle-${i}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                    <span className="text-[11px] text-slate-500">{t(`competitors:relation.${c.relation_type || "direct"}`)}</span>
                    <span className="text-[11px] text-slate-400">{Math.round((c.confidence || 0) * 100)}%</span>
                  </div>
                  {c.url && <p className="mt-0.5 truncate text-[11px] text-slate-500">{c.url}</p>}
                  {c.reason && <p className="mt-1 text-xs text-slate-500">{c.reason}</p>}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {(draft.sources || []).length > 0 && (
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <header className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("autofill:review.sources.title")}</h2>
            <p className="text-sm text-slate-500">{t("autofill:review.sources.subtitle")}</p>
          </header>
          <div className="space-y-2">
            {draft.sources.map((p, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <a href={p.url} target="_blank" rel="noreferrer" className="block truncate text-sm font-medium text-indigo-700 hover:text-indigo-900">
                    {p.title || p.url}
                  </a>
                  {p.excerpt && <p className="mt-1 text-xs text-slate-500">{p.excerpt}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => navigate("/brands")} data-testid="autofill-review-discard">
          {t("autofill:review.discard")}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="autofill-review-save" className="bg-slate-900 text-white hover:bg-slate-800">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} />{t("autofill:review.save")}</>}
        </Button>
      </div>
    </div>
  );
}
